import React, { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { Tv, MonitorPlay, ArrowLeft, CheckCircle2, Loader2, AlertCircle, Home, Clock, User, Car, BookOpen } from "lucide-react";
import { Seo, serviceJsonLd } from "../components/Seo";

const TV_BRANDS = ["Vet ej / osäker", "Samsung", "LG", "Sony", "Philips", "Panasonic", "TCL", "Hisense", "Annat märke"];
const PROVIDERS = ["Telia", "Tele2", "Comhem", "Allente", "Annan / vet ej"];

const CONFIG = {
  "smart-tv": {
    key: "smart_tv",
    Icon: Tv,
    title: "Hemkurs för Smart-TV",
    intro: "Vi kommer hem till dig och lär dig lugnt och tydligt hur just din Smart-TV fungerar – i din egen takt. Du får lära dig vilka funktioner som finns, installera appar, titta på streamingtjänster och casta från mobil eller surfplatta.",
    includes0: "Genomgång på plats (2–3 timmar). Vi går igenom din Smart-TV i din egen takt. Vi installerar appar, tittar på streamingtjänster, lär oss att casta från mobil/surfplatta och ställer in allt efter dina personliga önskemål.",
    field: "brand",
  },
  "tv-box": {
    key: "tv_box",
    Icon: MonitorPlay,
    title: "Hemkurs för TV-box",
    intro: "Vi kommer hem till dig och lär dig lugnt och tydligt hur just din TV-box fungerar – i din egen takt. Vi hjälper dig med din box från t.ex. Telia, Tele2, Comhem eller Allente: hur du navigerar i menyerna, hittar dina kanaler och använder eventuella play-tjänster.",
    includes0: "Genomgång på plats (2–3 timmar). Vi går igenom din TV-box i din egen takt. Vi lär oss navigera i menyerna, hitta dina kanaler, använda play-tjänster och ställer in allt efter dina personliga önskemål.",
    field: "provider",
  },
};

export default function CourseDetail() {
  const { service } = useParams();
  const cfg = CONFIG[service];

  const [form, setForm] = useState({ name: "", phone: "", postal_code: "", tv_brand: "", tv_providers: [] });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!cfg) return <Navigate to="/hemkurs" replace />;
  const { Icon } = cfg;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleProvider = (value) => setForm((f) => ({
    ...f,
    tv_providers: f.tv_providers.includes(value) ? f.tv_providers.filter((v) => v !== value) : [...f.tv_providers, value],
  }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.phone.trim() || !form.postal_code.trim()) {
      setError("Fyll i namn, telefonnummer och postnummer.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        name: form.name, phone: form.phone, postal_code: form.postal_code,
        service: cfg.key,
        tv_brand: cfg.field === "brand" ? form.tv_brand : "",
        tv_providers: cfg.field === "provider" ? form.tv_providers : [],
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError("Något gick fel. Försök igen om en stund.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-10 md:py-14">
      <Seo
        title={cfg.title}
        description={cfg.intro.split(". ")[0] + ". Personlig hemkurs i din egen takt – fast pris 1 995 kr inkl. moms, framkörning ingår."}
        path={`/hemkurs/${service}`}
        jsonLd={serviceJsonLd({ name: cfg.title, description: cfg.intro, path: `/hemkurs/${service}`, price: 1995 })}
      />
      <Link to="/hemkurs" data-testid="course-back" className="inline-flex items-center gap-2 text-lg font-semibold text-[#0056D2] hover:underline">
        <ArrowLeft size={22} strokeWidth={2.5} /> Tillbaka till kurserna
      </Link>

      <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-[#1A1A1A] mt-6 flex items-start gap-3">
        <Icon size={44} strokeWidth={2.5} className="text-[#0056D2] shrink-0 mt-1" /> {cfg.title}
      </h1>

      {done ? (
        <div data-testid="booking-success" className="bg-white border-2 border-gray-300 rounded-xl p-8 mt-8 text-center">
          <span className="inline-flex bg-green-50 text-[#0B7A3F] rounded-full p-4">
            <CheckCircle2 size={56} strokeWidth={2} />
          </span>
          <h2 className="font-heading text-3xl font-bold text-[#1A1A1A] mt-5">Tack för din intresseanmälan!</h2>
          <p className="text-lg text-[#4A4A4A] mt-3">
            Vi har tagit emot dina uppgifter och ringer upp dig på det telefonnummer du angav för att boka en tid som passar dig.
          </p>
          <Link to="/" className="inline-flex items-center gap-2 text-lg font-semibold text-[#0056D2] hover:underline mt-6">
            <Home size={22} strokeWidth={2.5} /> Tillbaka till start
          </Link>
        </div>
      ) : (
        <>
          <div className="bg-white border-2 border-gray-300 rounded-xl p-7 md:p-9 mt-8">
            <p className="text-lg leading-relaxed text-[#1A1A1A]">{cfg.intro}</p>
            <ul className="mt-6 space-y-3">
              {[
                { Icon: Home, t: "Hemma hos dig", d: "Kursen sker i ditt eget hem, med din egen utrustning." },
                { Icon: Clock, t: "I din egen takt", d: "Vi tar det lugnt och går igenom allt steg för steg." },
                { Icon: User, t: "Anpassat efter dig", d: "Vi utgår från vad just du vill lära dig." },
              ].map(({ Icon: I, t, d }) => (
                <li key={t} className="flex items-start gap-3">
                  <I size={26} strokeWidth={2} className="text-[#0056D2] shrink-0 mt-0.5" />
                  <span className="text-lg text-[#1A1A1A]"><strong>{t}.</strong> {d}</span>
                </li>
              ))}
            </ul>
          </div>

          <div data-testid="course-pricing" className="bg-white border-2 border-gray-300 rounded-xl p-7 md:p-9 mt-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A1A1A]">{cfg.title} – i hemmet</h2>
            <div className="flex items-baseline gap-3 mt-3">
              <span data-testid="course-price" className="font-heading text-4xl font-extrabold text-[#1A1A1A]">1 995 kr</span>
              <span className="text-lg text-[#4A4A4A]">inkl. moms</span>
            </div>

            <h3 className="font-heading text-xl font-bold text-[#1A1A1A] mt-6">Detta ingår:</h3>
            <ul className="mt-4 space-y-5">
              <li className="flex items-start gap-4">
                <Clock size={28} strokeWidth={2} className="text-[#0056D2] shrink-0 mt-1" />
                <span className="text-lg text-[#1A1A1A]"><strong>{cfg.includes0.split(".")[0]}.</strong>{cfg.includes0.slice(cfg.includes0.indexOf(".") + 1)}</span>
              </li>
              <li className="flex items-start gap-4">
                <Car size={28} strokeWidth={2} className="text-[#0056D2] shrink-0 mt-1" />
                <span className="text-lg text-[#1A1A1A]">
                  <strong>Framkörning ingår.</strong> Fri bilresa och inställelse inom hela Stockholms län
                  (inklusive trängselskatt och parkering).
                </span>
              </li>
              <li className="flex items-start gap-4">
                <BookOpen size={28} strokeWidth={2} className="text-[#0056D2] shrink-0 mt-1" />
                <span className="text-lg text-[#1A1A1A]">
                  <strong>Anpassat material.</strong> Du får testa allt själv så att du känner dig trygg
                  med fjärrkontrollen när kursen är slut.
                </span>
              </li>
            </ul>
          </div>

          <form onSubmit={submit} className="bg-white border-2 border-gray-300 rounded-xl p-7 md:p-9 mt-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A1A1A]">Fyll i dina uppgifter</h2>
            <p className="text-base text-[#4A4A4A] mt-2">Så ringer vi upp dig och bokar en tid. Det tar bara en minut.</p>

            <div className="space-y-6 mt-6">
              <div>
                <label htmlFor="b-name" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Namn</label>
                <input id="b-name" data-testid="booking-name" value={form.name} onChange={set("name")}
                  className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder="För- och efternamn" />
              </div>
              <div>
                <label htmlFor="b-phone" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Telefonnummer</label>
                <input id="b-phone" data-testid="booking-phone" type="tel" value={form.phone} onChange={set("phone")}
                  className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder="07X-XXX XX XX" />
              </div>
              <div>
                <label htmlFor="b-postal" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Postnummer</label>
                <input id="b-postal" data-testid="booking-postal" value={form.postal_code} onChange={set("postal_code")}
                  className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder="XXX XX" />
              </div>

              {cfg.field === "brand" && (
                <div data-testid="brand-section">
                  <label htmlFor="b-brand" className="text-xl font-bold mb-2 block text-[#1A1A1A]">
                    Vilket märke är din Smart-TV? <span className="text-base font-medium text-[#4A4A4A]">(om du vet)</span>
                  </label>
                  <select id="b-brand" data-testid="booking-brand" value={form.tv_brand} onChange={set("tv_brand")}
                    className="min-h-[56px] text-lg px-3 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2] bg-white">
                    <option value="">Välj i listan…</option>
                    {TV_BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
              )}

              {cfg.field === "provider" && (
                <div data-testid="provider-section">
                  <label className="text-xl font-bold mb-3 block text-[#1A1A1A]">
                    Vilken TV-leverantör har du? <span className="text-base font-medium text-[#4A4A4A]">(kryssa i)</span>
                  </label>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {PROVIDERS.map((p) => {
                      const active = form.tv_providers.includes(p);
                      return (
                        <button key={p} type="button" data-testid={`provider-${p}`} onClick={() => toggleProvider(p)}
                          aria-pressed={active}
                          className={`flex items-center gap-3 min-h-[60px] px-5 rounded-lg border-2 text-lg font-semibold transition-colors ${active ? "border-[#0056D2] bg-blue-50 text-[#0056D2]" : "border-gray-400 bg-white text-[#1A1A1A] hover:bg-gray-50"}`}>
                          <span className={`flex items-center justify-center w-7 h-7 rounded border-2 ${active ? "border-[#0056D2] bg-[#0056D2] text-white" : "border-gray-400"}`}>
                            {active && <CheckCircle2 size={20} strokeWidth={3} />}
                          </span>
                          {p}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {error && (
                <p data-testid="booking-error" className="flex items-center gap-2 text-[#D32F2F] text-lg font-semibold">
                  <AlertCircle size={22} /> {error}
                </p>
              )}

              <button data-testid="booking-submit" type="submit" disabled={submitting}
                className="inline-flex items-center justify-center gap-3 min-h-[64px] w-full px-8 bg-[#0056D2] text-white text-xl font-bold rounded-lg hover:bg-[#003E99] transition-colors disabled:opacity-60">
                {submitting ? <Loader2 className="animate-spin" size={26} /> : <Icon size={26} strokeWidth={2.5} />}
                {submitting ? "Skickar…" : "Skicka min intresseanmälan"}
              </button>
              <p className="text-base text-[#4A4A4A] text-center">Du bokar inget bindande här – vi ringer upp och bestämmer en tid tillsammans.</p>
              <p data-testid="gdpr-notice" className="text-sm text-[#4A4A4A] leading-relaxed text-center">
                Din integritet är viktig för oss. Vi delar aldrig dina uppgifter med tredje part och hanterar all data säkert i enlighet med GDPR.
              </p>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
